import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { themesArray, getTheme } from './themes';
import { Check, Palette, LayoutList, LayoutGrid, ExternalLink } from 'lucide-react';
import { API } from './utils/apiConfig';

export default function AppearanceSettings() {
  const [currentTheme, setCurrentTheme] = useState('default');
  const [currentLayout, setCurrentLayout] = useState('list');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [restaurant, setRestaurant] = useState(null);
  const [showCustomPalette, setShowCustomPalette] = useState(false);
  const [customColors, setCustomColors] = useState({
    primary: '#3B82F6',
    primaryHover: '#2563EB',
    primaryLight: '#EFF6FF',
    accent: '#60A5FA',
    success: '#10B981'
  });

  useEffect(() => {
    loadCurrentTheme();
  }, []);

  const loadCurrentTheme = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Carregar dados do restaurante
      const restaurantData = localStorage.getItem('restaurant');
      if (restaurantData) {
        setRestaurant(JSON.parse(restaurantData));
      }
      
      const response = await axios.get(`${API}/restaurant/appearance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentTheme(response.data.theme || 'default');
      setCurrentLayout(response.data.layout || 'list');
    } catch (error) {
      console.error('Error loading theme:', error);
      setCurrentTheme('default');
      setCurrentLayout('list');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = async (themeId) => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/restaurant/appearance`,
        { theme: themeId, layout: currentLayout },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentTheme(themeId);
      setMessage({
        type: 'success',
        text: '✅ Tema atualizado com sucesso! As mudanças já estão visíveis no seu cardápio.'
      });
      
      // Clear message after 5 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      
    } catch (error) {
      console.error('Error updating theme:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao atualizar tema';
      
      setMessage({
        type: 'error',
        text: `❌ Erro: ${errorMsg}`
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLayoutChange = async (layout) => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/restaurant/appearance`,
        { theme: currentTheme, layout: layout },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentLayout(layout);
      setMessage({
        type: 'success',
        text: `✅ Layout alterado para ${layout === 'list' ? 'Lista' : 'Grade'}! As mudanças já estão visíveis no seu cardápio.`
      });
      
      // Clear message after 5 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      
    } catch (error) {
      console.error('Error updating layout:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao atualizar layout';
      
      setMessage({
        type: 'error',
        text: `❌ Erro: ${errorMsg}`
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCustomColorChange = (colorKey, value) => {
    setCustomColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
  };

  const applyCustomPalette = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/restaurant/appearance`,
        { 
          theme: 'custom', 
          layout: currentLayout,
          custom_colors: customColors
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentTheme('custom');
      setMessage({
        type: 'success',
        text: '✅ Paleta personalizada aplicada com sucesso!'
      });
      
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      
    } catch (error) {
      console.error('Error applying custom palette:', error);
      setMessage({
        type: 'error',
        text: `❌ Erro ao aplicar paleta personalizada`
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Palette className="w-8 h-8 text-blue-500" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Personalização</h1>
              <p className="text-gray-300 text-sm md:text-base">
                Tema e aparência do cardápio
              </p>
            </div>
          </div>
          
          {/* Botão Ver Cardápio Público */}
          {restaurant?.subdomain && (
            <button
              onClick={() => window.open(`${window.location.origin}/menu/${restaurant.subdomain}`, '_blank')}
              className="glass-button-primary flex items-center justify-center gap-2 whitespace-nowrap px-4 py-3 text-sm md:text-base font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="view-public-menu-button"
            >
              <ExternalLink size={18} />
              <span>Ver Cardápio Público</span>
            </button>
          )}
        </div>
      </div>

      {/* Success/Error Message */}
      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Layout Selection Section */}
      <div className="mb-10">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">📐 Layout de Produtos</h2>
          <p className="text-gray-300">
            Escolha como os produtos serão exibidos no seu cardápio público
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {/* Lista Layout */}
          <div
            className={`relative bg-white rounded-xl border-2 transition-all duration-300 cursor-pointer hover:shadow-xl ${
              currentLayout === 'list'
                ? 'border-blue-500 shadow-lg scale-105'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => !saving && handleLayoutChange('list')}
          >
            {/* Active Badge */}
            {currentLayout === 'list' && (
              <div className="absolute top-3 right-3 z-10">
                <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                  <Check className="w-3 h-3" />
                  ATUAL
                </div>
              </div>
            )}

            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <LayoutList className="w-8 h-8 text-blue-600" />
                <div>
                  <h3 className="text-xl font-bold text-gray-800">📋 Lista</h3>
                  <p className="text-sm text-gray-700">Ideal para cardápios digitais</p>
                </div>
              </div>

              <p className="text-sm text-gray-700 mb-4">
                Layout tradicional com produtos em lista vertical. Melhor legibilidade e foco nas descrições.
              </p>

              {/* Preview Visual */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="space-y-2">
                  <div className="bg-white rounded-md p-3 flex items-center gap-3 shadow-sm">
                    <div className="w-12 h-12 bg-gray-300 rounded"></div>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-400 rounded w-3/4 mb-1"></div>
                      <div className="h-2 bg-gray-300 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-md p-3 flex items-center gap-3 shadow-sm">
                    <div className="w-12 h-12 bg-gray-300 rounded"></div>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-400 rounded w-3/4 mb-1"></div>
                      <div className="h-2 bg-gray-300 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-md p-3 flex items-center gap-3 shadow-sm">
                    <div className="w-12 h-12 bg-gray-300 rounded"></div>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-400 rounded w-3/4 mb-1"></div>
                      <div className="h-2 bg-gray-300 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Grade Layout */}
          <div
            className={`relative bg-white rounded-xl border-2 transition-all duration-300 cursor-pointer hover:shadow-xl ${
              currentLayout === 'grid'
                ? 'border-blue-500 shadow-lg scale-105'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => !saving && handleLayoutChange('grid')}
          >
            {/* Active Badge */}
            {currentLayout === 'grid' && (
              <div className="absolute top-3 right-3 z-10">
                <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                  <Check className="w-3 h-3" />
                  ATUAL
                </div>
              </div>
            )}

            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <LayoutGrid className="w-8 h-8 text-blue-600" />
                <div>
                  <h3 className="text-xl font-bold text-gray-800">🛍️ Grade</h3>
                  <p className="text-sm text-gray-700">Ideal para lojas virtuais</p>
                </div>
              </div>

              <p className="text-sm text-gray-700 mb-4">
                Layout moderno com carrossel. Destaque para imagens e navegação dinâmica. Arraste para ver mais produtos.
              </p>

              {/* Preview Visual */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex gap-2 overflow-hidden">
                  <div className="bg-white rounded-md p-2 flex-shrink-0 shadow-sm" style={{width: '70px'}}>
                    <div className="w-full h-12 bg-gray-300 rounded mb-1"></div>
                    <div className="h-2 bg-gray-400 rounded w-full mb-1"></div>
                    <div className="h-2 bg-gray-300 rounded w-2/3"></div>
                  </div>
                  <div className="bg-white rounded-md p-2 flex-shrink-0 shadow-sm" style={{width: '70px'}}>
                    <div className="w-full h-12 bg-gray-300 rounded mb-1"></div>
                    <div className="h-2 bg-gray-400 rounded w-full mb-1"></div>
                    <div className="h-2 bg-gray-300 rounded w-2/3"></div>
                  </div>
                  <div className="bg-white rounded-md p-2 flex-shrink-0 shadow-sm" style={{width: '70px'}}>
                    <div className="w-full h-12 bg-gray-300 rounded mb-1"></div>
                    <div className="h-2 bg-gray-400 rounded w-full mb-1"></div>
                    <div className="h-2 bg-gray-300 rounded w-2/3"></div>
                  </div>
                  <div className="bg-white rounded-md p-2 flex-shrink-0 shadow-sm opacity-50" style={{width: '40px'}}>
                    <div className="w-full h-12 bg-gray-300 rounded mb-1"></div>
                  </div>
                </div>
                <div className="text-center text-xs text-gray-500 mt-2">← Arraste →</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-10"></div>

      {/* Theme Section Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">🎨 Tema de Cores</h2>
        <p className="text-gray-300">
          Escolha a paleta de cores do seu estabelecimento
        </p>
      </div>

      {/* Theme Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {themesArray.map((theme) => {
          const isActive = currentTheme === theme.id;
          
          return (
            <div
              key={theme.id}
              className={`relative bg-white rounded-xl border-2 transition-all duration-300 overflow-hidden cursor-pointer hover:shadow-xl ${
                isActive
                  ? 'border-blue-500 shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => !saving && handleThemeChange(theme.id)}
            >
              {/* Active Badge */}
              {isActive && (
                <div className="absolute top-3 right-3 z-10">
                  <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                    <Check className="w-3 h-3" />
                    ATUAL
                  </div>
                </div>
              )}

              {/* Theme Preview */}
              <div className="p-6">
                {/* Icon and Name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">{theme.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{theme.name}</h3>
                    <p className="text-xs text-gray-700">{theme.category}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-700 mb-4 min-h-[40px]">
                  {theme.description}
                </p>

                {/* Color Palette Preview */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    Paleta de Cores
                  </p>
                  <div className="flex gap-2">
                    <div
                      className="h-12 flex-1 rounded-lg shadow-sm"
                      style={{ backgroundColor: theme.colors.primary }}
                      title="Primary"
                    />
                    <div
                      className="h-12 flex-1 rounded-lg shadow-sm"
                      style={{ backgroundColor: theme.colors.accent }}
                      title="Accent"
                    />
                    <div
                      className="h-12 flex-1 rounded-lg shadow-sm"
                      style={{ backgroundColor: theme.colors.success }}
                      title="Success"
                    />
                  </div>
                </div>

                {/* Button Preview */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Preview de Botões
                  </p>
                  <button
                    className="w-full py-2.5 rounded-lg font-semibold text-white text-sm transition-all shadow-sm"
                    style={{
                      backgroundColor: theme.colors.primary,
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme.colors.primaryHover;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = theme.colors.primary;
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Adicionar ao Carrinho
                  </button>
                </div>
              </div>

              {/* Select Overlay */}
              {!isActive && (
                <div className="absolute inset-0 bg-white/0 hover:bg-blue-500/5 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                  <button
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:bg-blue-600 transition-all"
                    disabled={saving}
                  >
                    {saving ? 'Aplicando...' : 'Selecionar Tema'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-10"></div>

      {/* Custom Palette Section */}
      <div className="mb-10">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">🎨 Paleta Personalizada</h2>
          <p className="text-gray-300">
            Crie sua própria combinação de cores única
          </p>
        </div>

        <button
          onClick={() => setShowCustomPalette(!showCustomPalette)}
          className="mb-6 px-6 py-3 rounded-xl font-semibold text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, #9333EA 0%, #EC4899 100%)'
          }}
          data-testid="toggle-custom-palette-btn"
        >
          {showCustomPalette ? '✕ Fechar Editor' : '🎨 Personalizar Cores'}
        </button>

        {showCustomPalette && (
          <div className="bg-white rounded-xl p-6 border-2 border-purple-200 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
              {/* Primary Color */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  🎯 Cor Principal
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customColors.primary}
                    onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    data-testid="color-picker-primary"
                  />
                  <input
                    type="text"
                    value={customColors.primary}
                    onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono text-gray-800"
                    placeholder="#3B82F6"
                  />
                </div>
                <p className="text-xs text-gray-500">Botões e destaques</p>
              </div>

              {/* Primary Hover */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  👆 Hover Principal
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customColors.primaryHover}
                    onChange={(e) => handleCustomColorChange('primaryHover', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    data-testid="color-picker-primary-hover"
                  />
                  <input
                    type="text"
                    value={customColors.primaryHover}
                    onChange={(e) => handleCustomColorChange('primaryHover', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono text-gray-800"
                    placeholder="#2563EB"
                  />
                </div>
                <p className="text-xs text-gray-500">Ao passar o mouse</p>
              </div>

              {/* Primary Light */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  💡 Fundo Claro
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customColors.primaryLight}
                    onChange={(e) => handleCustomColorChange('primaryLight', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    data-testid="color-picker-primary-light"
                  />
                  <input
                    type="text"
                    value={customColors.primaryLight}
                    onChange={(e) => handleCustomColorChange('primaryLight', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono text-gray-800"
                    placeholder="#EFF6FF"
                  />
                </div>
                <p className="text-xs text-gray-500">Backgrounds suaves</p>
              </div>

              {/* Accent */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  ✨ Cor de Acento
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customColors.accent}
                    onChange={(e) => handleCustomColorChange('accent', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    data-testid="color-picker-accent"
                  />
                  <input
                    type="text"
                    value={customColors.accent}
                    onChange={(e) => handleCustomColorChange('accent', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono text-gray-800"
                    placeholder="#60A5FA"
                  />
                </div>
                <p className="text-xs text-gray-500">Elementos secundários</p>
              </div>

              {/* Success */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  ✅ Cor de Sucesso
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customColors.success}
                    onChange={(e) => handleCustomColorChange('success', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    data-testid="color-picker-success"
                  />
                  <input
                    type="text"
                    value={customColors.success}
                    onChange={(e) => handleCustomColorChange('success', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono text-gray-800"
                    placeholder="#10B981"
                  />
                </div>
                <p className="text-xs text-gray-500">Confirmações e preços</p>
              </div>
            </div>

            {/* Live Preview */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">👁️ Preview em Tempo Real</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Color Palette Preview */}
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Paleta de Cores</p>
                  <div className="flex gap-2">
                    <div
                      className="h-16 flex-1 rounded-lg shadow-md flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: customColors.primary }}
                    >
                      Principal
                    </div>
                    <div
                      className="h-16 flex-1 rounded-lg shadow-md flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: customColors.primaryHover }}
                    >
                      Hover
                    </div>
                    <div
                      className="h-16 flex-1 rounded-lg shadow-md flex items-center justify-center text-gray-700 text-xs font-bold border"
                      style={{ backgroundColor: customColors.primaryLight }}
                    >
                      Claro
                    </div>
                    <div
                      className="h-16 flex-1 rounded-lg shadow-md flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: customColors.accent }}
                    >
                      Acento
                    </div>
                    <div
                      className="h-16 flex-1 rounded-lg shadow-md flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: customColors.success }}
                    >
                      Sucesso
                    </div>
                  </div>
                </div>

                {/* Buttons Preview */}
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Preview de Botões</p>
                  <div className="space-y-2">
                    <button
                      className="w-full py-3 rounded-lg font-semibold text-white transition-all shadow-md"
                      style={{ backgroundColor: customColors.primary }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = customColors.primaryHover}
                      onMouseLeave={(e) => e.target.style.backgroundColor = customColors.primary}
                    >
                      Adicionar ao Carrinho
                    </button>
                    <div 
                      className="p-4 rounded-lg border"
                      style={{ backgroundColor: customColors.primaryLight }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-800">Produto Exemplo</span>
                        <span 
                          className="font-bold"
                          style={{ color: customColors.success }}
                        >
                          R$ 29,90
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={applyCustomPalette}
                disabled={saving}
                className="w-full py-4 rounded-xl font-bold text-white text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-102 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: `linear-gradient(135deg, ${customColors.primary} 0%, ${customColors.accent} 100%)`
                }}
                data-testid="apply-custom-palette-btn"
              >
                {saving ? '⏳ Aplicando...' : '✨ Aplicar Paleta Personalizada'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>💡 Dica:</strong> As mudanças são aplicadas instantaneamente no seu cardápio digital. 
          Abra seu menu público em outra aba para ver o resultado em tempo real!
        </p>
      </div>
    </div>
  );
}
